import React from 'react';
import { Loader2, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
import logoEkose from '../../../images/logoekose.svg';

export interface LoaderProps {
  variant?: 'spinner' | 'logo' | 'dots' | 'pulse' | 'bars';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'white' | 'current';
  className?: string;
  text?: string;
  fullScreen?: boolean;
  overlay?: boolean;
  state?: 'idle' | 'loading' | 'success' | 'error' | 'timeout';
  message?: string;
}

export const Loader: React.FC<LoaderProps> = ({
  variant = 'spinner',
  size = 'md',
  color = 'primary',
  className = '',
  text,
  fullScreen = false,
  overlay = false,
  state,
  message,
}) => {
  // Map state to color and icon/variant
  const finalColor = state === 'success' ? 'success' : 
                   state === 'error' ? 'danger' : 
                   state === 'timeout' ? 'warning' : color;
  
  const finalText = message || text;

  const sizeMap = {
    xs: 12,
    sm: 16,
    md: 24,
    lg: 32,
    xl: 48,
  };

  const pixelSize = typeof size === 'number' ? size : sizeMap[size];

  const colorClasses = {
    primary: 'text-med-primary',
    success: 'text-med-success',
    warning: 'text-med-warning',
    danger: 'text-med-danger',
    white: 'text-white',
    current: 'text-current',
  };

  const renderContent = () => {
    // If we have a state like success/error/timeout, show icon instead of spinner
    if (state && state !== 'loading' && state !== 'idle') {
      switch (state) {
        case 'success': return <CheckCircle size={pixelSize} className={colorClasses.success} />;
        case 'error': return <AlertCircle size={pixelSize} className={colorClasses.danger} />;
        case 'timeout': return <Clock size={pixelSize} className={colorClasses.warning} />;
      }
    }

    switch (variant) {
      case 'logo':
        return (
          <img
            src={logoEkose}
            alt="Chargement..."
            style={{ width: pixelSize, height: pixelSize }}
            className={cn('animate-spin-slow', className)}
          />
        );
      case 'dots':
        return (
          <div className={cn('flex space-x-1 items-center', className)}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={cn(
                  'rounded-full animate-bounce',
                  colorClasses[finalColor].replace('text-', 'bg-')
                )}
                style={{
                  width: pixelSize / 3,
                  height: pixelSize / 3,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>
        );
      case 'pulse':
        return (
          <div
            className={cn(
              'rounded-full animate-pulse',
              colorClasses[finalColor].replace('text-', 'bg-'),
              className
            )}
            style={{ width: pixelSize, height: pixelSize }}
          />
        );
      case 'bars':
        return (
          <div className={cn('flex space-x-1 items-end', className)}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={cn('bg-current animate-pulse rounded-sm', colorClasses[finalColor])}
                style={{
                  width: pixelSize / 4,
                  height: pixelSize,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>
        );
      default: // spinner
        return (
          <Loader2
            size={pixelSize}
            className={cn('animate-spin', colorClasses[finalColor], className)}
          />
        );
    }
  };

  const content = (
    <div className={cn('flex flex-col items-center justify-center gap-3', !fullScreen && !overlay && 'inline-flex')}>
      {renderContent()}
      {finalText && (
        <p className={cn('text-sm font-medium', state === 'loading' && 'animate-pulse', colorClasses[finalColor])}>
          {finalText}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-surface-page/90 backdrop-blur-sm flex items-center justify-center z-[9999]">
        {content}
      </div>
    );
  }

  if (overlay) {
    return (
      <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-50 rounded-[inherit]">
        {content}
      </div>
    );
  }

  return content;
};

// Aliases for backward compatibility or semantic usage
export const LoadingSpinner = (props: LoaderProps) => <Loader variant="spinner" {...props} />;
export const Spinner = (props: LoaderProps) => <Loader variant="spinner" {...props} />;
export const LogoLoader = (props: LoaderProps) => <Loader variant="logo" {...props} />;
export const FullScreenLoader = (props: LoaderProps) => <Loader fullScreen {...props} />;

export default Loader;
